package uk.co.nickthecoder.kyd.util

import org.khronos.webgl.*
import org.khronos.webgl.WebGLRenderingContext.Companion.COLOR_ATTACHMENT0
import org.khronos.webgl.WebGLRenderingContext.Companion.COLOR_BUFFER_BIT
import org.khronos.webgl.WebGLRenderingContext.Companion.FRAMEBUFFER
import org.khronos.webgl.WebGLRenderingContext.Companion.FRAMEBUFFER_COMPLETE
import org.khronos.webgl.WebGLRenderingContext.Companion.FUNC_ADD
import org.khronos.webgl.WebGLRenderingContext.Companion.NEAREST
import org.khronos.webgl.WebGLRenderingContext.Companion.RGBA
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_2D
import org.khronos.webgl.WebGLRenderingContext.Companion.TEXTURE_MIN_FILTER
import org.khronos.webgl.WebGLRenderingContext.Companion.UNPACK_ALIGNMENT
import org.khronos.webgl.WebGLRenderingContext.Companion.UNSIGNED_BYTE
import uk.co.nickthecoder.glok.backend.webgl.WebGLBackend
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.util.Matrix
import kotlin.math.max
import kotlin.math.min

internal actual object PixelDetection {

    var debug = false

    /**
     * A 1x1 texture which will be the destination.
     */
    private val pixelTextureID = WebGLBackend.gl.createTexture() !!
    private val pixelFrameBufferId = WebGLBackend.gl.createFramebuffer() !!

    // Overlapping

    private val overlapATextureID = WebGLBackend.gl.createTexture() !!
    private val overlapAFrameBufferId = WebGLBackend.gl.createFramebuffer() !!

    private val overlapBTextureID = WebGLBackend.gl.createTexture() !!
    private val overlapBFrameBufferId = WebGLBackend.gl.createFramebuffer() !!

    private val overlapCTextureID = WebGLBackend.gl.createTexture() !!
    private val overlapCFrameBufferId = WebGLBackend.gl.createFramebuffer() !!

    /**
     * The size of internal textures when detecting pixel overlap.
     * If the axis-aligned bounding boxes intersect over a larger area than this, then the pixel detection is
     * scaled down to fit.
     * In the vast number of cases, these dimensions can be small without any ill effects.
     * However, if the potential overlap is large and the shapes are thin in the overlapping region,
     * then false-negatives can occur.
     *
     * For example, suppose we need to scale down by a factor of 16. A 1 pixel wide line will result in an
     * alpha channel value 1/16th of the "expected" value.
     */
    private const val MAX_WIDTH = 16 // TODO What is a sensible size? Should this be parameterised?
    private const val MAX_HEIGHT = 16

    private val minMaxExtension = WebGLBackend.gl.getExtension("EXT_blend_minmax")

    private val pixelData = Uint8Array(Array<Byte>(MAX_WIDTH * MAX_HEIGHT * 4) { (0).toByte() })

    init {
        initialiseFrameBuffer(pixelFrameBufferId, pixelTextureID, 1, 1)

        initialiseFrameBuffer(overlapAFrameBufferId, overlapATextureID, MAX_WIDTH, MAX_HEIGHT)
        initialiseFrameBuffer(overlapBFrameBufferId, overlapBTextureID, MAX_WIDTH, MAX_HEIGHT)
        initialiseFrameBuffer(overlapCFrameBufferId, overlapCTextureID, MAX_WIDTH, MAX_HEIGHT)

        backend.reportError()
    }

    private val FUNC_MIN: Int = (minMaxExtension.MIN_EXT) as Int

    private fun initialiseFrameBuffer(
        frameBufferID: WebGLFramebuffer,
        textureID: WebGLTexture,
        width: Int,
        height: Int
    ) {
        with(WebGLBackend.gl) {
            println("Creating texture and frame buffer $width x $height")
            bindTexture(TEXTURE_2D, textureID)
            texParameteri(TEXTURE_2D, TEXTURE_MIN_FILTER, NEAREST)
            val data = Array(width * height * 4) { (0).toByte() }
            texImage2D(TEXTURE_2D, 0, RGBA, width, height, 0, RGBA, UNSIGNED_BYTE, Uint8Array(data))

            // Set up the frame buffer
            bindFramebuffer(FRAMEBUFFER, frameBufferID)
            framebufferTexture2D(FRAMEBUFFER, COLOR_ATTACHMENT0, TEXTURE_2D, textureID, 0)
            bindFramebuffer(FRAMEBUFFER, null)
            backend.reportError()
            val canRead = (checkFramebufferStatus(FRAMEBUFFER) == FRAMEBUFFER_COMPLETE)
            println("Done $canRead")
        }
    }

    actual fun actorColorAt(actor: Actor, world: Vector2): Color {
        with(WebGLBackend.gl) {
            // Begin writing to the pixel frame buffer rather than the screen's frame buffer.
            bindTexture(TEXTURE_2D, null)
            bindFramebuffer(FRAMEBUFFER, pixelFrameBufferId)

            // Clear
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            viewport(0, 0, 1, 1)

            // Draw the actor
            backend.drawView(1f, 1f, 1f) {
                // TODO Do we need a 0.5 pixel correction? Test with a 1x1 pixel, and if we are off, then the result
                // will be half or a quarter what we expect.
                backend.transform(Matrix.translation(- world.x, - world.y)) {
                    actor.appearance.draw(actor.localToWorld)
                }
            }
            backend.reportError()

            // Read the pixel from the frame buffer.
            // TODO This fails on Firefox 128.13.0esr : WebGL warning: readPixels: Out-of-bounds reads with readPixels are deprecated, and may be slow.
            // Seems to work fine on Chromium and Brave.
            // NOTE. If I make the buffer too small, I get an additional error.
            // So the error seems to refer to the region i.e. (0,0) size (1x1). Hmm.
            readPixels(x = 0, y = 0, width = 1, height = 1, format = RGBA, type = UNSIGNED_BYTE, pixelData)
            backend.reportError()

            // Convert unsigned bytes to floats in the range 0..1
            val color = Color(
                (pixelData[0].toInt() and 0xff).toFloat() / 255,
                (pixelData[1].toInt() and 0xff).toFloat() / 255,
                (pixelData[2].toInt() and 0xff).toFloat() / 255,
                (pixelData[3].toInt() and 0xff).toFloat() / 255
            )

            bindFramebuffer(FRAMEBUFFER, null)
            bindTexture(TEXTURE_2D, null)

            return color
        }
    }


    actual fun overlapping(actorA: Actor, actorB: Actor, threshold: Int): Boolean {

        val appA = actorA.appearance
        val bounds = appA.localBounds()
        val leftA = min(bounds.first.x, bounds.second.x)
        val rightA = max(bounds.first.x, bounds.second.x)
        val topA = max(bounds.first.y, bounds.second.y)
        val bottomA = min(bounds.first.y, bounds.second.y)

        val bLocalToALocal = actorA.worldToLocal * actorB.localToWorld

        val appB = actorB.appearance
        // corners of B in A's local coordinates
        val cornersB = appB.localCorners().map { it * bLocalToALocal }
        if (cornersB.isEmpty()) return false
        val leftB = cornersB.minOf { it.x }
        val rightB = cornersB.maxOf { it.x }
        val topB = cornersB.maxOf { it.y }
        val bottomB = cornersB.minOf { it.y }

        // Calculate the area (in A's local coordinates) of the potential overlap
        val left = max(leftA, leftB)
        val right = min(rightA, rightB)
        val top = min(topA, topB)
        val bottom = max(bottomA, bottomB)


        if (right - left < 0) return false
        if (top - bottom < 0) return false

        // If the size of the overlap is larger than our texture, we will need to scale it (via the view matrix?)
        // This will lose fidelity, but shouldn't be impactful.
        val scaleX = min(1f, MAX_WIDTH / (right - left))
        val scaleY = min(1f, MAX_HEIGHT / (top - bottom))
        val scaled = scaleX < 1f || scaleY < 1f

        // So now we need to draw actor A, so that `left`, `top` is in the top left of the destination texture.
        // We then draw actor B using "MAX" blend.
        // Then read the alpha channel, and return true if any value > threshold.

        // We need to translate so that $left, $top is at (0,0) of the destination texture.
        // MAX_HEIGHT - ... is needed because of the confusion between Kyd's Y UP axis, and Glok's Y DOWN axis.
        val transformation = if (scaled) {
            Matrix.scale(scaleX, scaleY).translate(- left, (MAX_HEIGHT / scaleY - top))
        } else {
            Matrix.translation(- left, MAX_HEIGHT - top)
        }

        with(WebGLBackend.gl) {
            // Draw Actor A to a texture
            bindTexture(TEXTURE_2D, null)
            bindFramebuffer(FRAMEBUFFER, overlapAFrameBufferId)
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            viewport(0, 0, MAX_WIDTH, MAX_HEIGHT)
            backend.drawView(MAX_WIDTH.toFloat(), MAX_HEIGHT.toFloat(), 1f) {
                backend.transform(transformation) {
                    actorA.appearance.draw(Matrix.IDENTITY)
                }
            }

            // Draw Actor B to a texture
            bindTexture(TEXTURE_2D, null)
            bindFramebuffer(FRAMEBUFFER, overlapBFrameBufferId)
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            viewport(0, 0, MAX_WIDTH, MAX_HEIGHT)
            backend.drawView(MAX_WIDTH.toFloat(), MAX_HEIGHT.toFloat(), 1f) {
                backend.transform(transformation) {
                    actorB.appearance.draw(bLocalToALocal)
                }
            }

            // Combine textures A and B onto texture C
            bindFramebuffer(FRAMEBUFFER, overlapCFrameBufferId)
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            // Draw actorA
            viewport(0, 0, MAX_WIDTH, MAX_HEIGHT)
            PlainTextureProgram.blitAll(overlapATextureID)
            // Now draw actorB, but use the MINIMUM of the existing value and the to-be-drawn value.
            // The next result, only in places were the actors overlap will have a non-zero alpha value.
            blendEquation(FUNC_MIN)
            PlainTextureProgram.blitAll(overlapBTextureID)
            blendEquation(FUNC_ADD)


            val result = scanForOverlappingPixels(
                min(MAX_WIDTH, (right - left).toInt()),
                min(MAX_HEIGHT, (top - bottom).toInt()),
                threshold
            )
            // Stop using the frame buffer, and return to rendering to the window.
            bindFramebuffer(FRAMEBUFFER, null)

            // TODO Optimisation :
            // Copy the right side to the left side (using MAX blend)
            // Copy the bottom left to the top left
            // Repeat, halving the rectangle size by 2 each time.
            // Then copy the result to a 1x1 texture, and read the result.
            // In this way, we don't need to transfer the entire texture from GPU to CPU.

            return result
        }
    }

    actual fun overlapping(actorA: Actor, others: Iterable<Actor>, threshold: Int): Boolean {

        val appA = actorA.appearance
        val aLocalToWorld = actorA.localToWorld

        val cornersA = appA.localCorners().map { it * aLocalToWorld }
        if (cornersA.isEmpty()) return false
        val leftA = cornersA.minOf { it.x }
        val rightA = cornersA.maxOf { it.x }
        val topA = cornersA.maxOf { it.y }
        val bottomA = cornersA.minOf { it.y }

        if (rightA - leftA <= 0) return false
        if (topA - bottomA <= 0) return false

        // If the size of the overlap is larger than our texture, we will need to scale it (via the view matrix?)
        // This will lose fidelity, but shouldn't be impactful.
        val scaleX = min(1f, MAX_WIDTH / (rightA - leftA))
        val scaleY = min(1f, MAX_HEIGHT / (topA - bottomA))
        val scaled = scaleX < 1f || scaleY < 1f

        // We need to translate so that leftA, topA is at (0,0) of the destination texture.
        // MAX_HEIGHT - ... is needed because of the confusion between Kyd's Y UP axis, and Glok's Y DOWN axis.
        val transformation = if (scaled) {
            Matrix.scale(scaleX, scaleY).translate(- leftA, (MAX_HEIGHT / scaleY - topA))
        } else {
            Matrix.translation(- leftA, MAX_HEIGHT - topA)
        }

        with(WebGLBackend.gl) {
            // Draw Actor A to a texture
            bindTexture(TEXTURE_2D, null)
            bindFramebuffer(FRAMEBUFFER, overlapAFrameBufferId)
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            viewport(0, 0, MAX_WIDTH, MAX_HEIGHT)
            backend.drawView(MAX_WIDTH.toFloat(), MAX_HEIGHT.toFloat(), 1f) {
                backend.transform(transformation) {
                    actorA.appearance.draw(actorA.localToWorld)
                }
            }


            // Draw other actors to a texture
            bindTexture(TEXTURE_2D, null)
            bindFramebuffer(FRAMEBUFFER, overlapBFrameBufferId)
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            viewport(0, 0, MAX_WIDTH, MAX_HEIGHT)

            backend.drawView(MAX_WIDTH.toFloat(), MAX_HEIGHT.toFloat(), 1f) {
                backend.transform(transformation) {

                    for (actorB in others) {
                        if (actorB === actorA) continue
                        val appB = actorB.appearance
                        // corners of B in world coordinates
                        val cornersB = appB.localCorners().map { it * actorB.localToWorld }
                        if (cornersB.isEmpty()) continue
                        val leftB = cornersB.minOf { it.x }
                        val rightB = cornersB.maxOf { it.x }
                        val topB = cornersB.maxOf { it.y }
                        val bottomB = cornersB.minOf { it.y }
                        if (leftB > rightA) continue
                        if (rightB < leftA) continue
                        if (topB < bottomA) continue
                        if (bottomB > topA) continue

                        actorB.appearance.draw(actorB.localToWorld)
                    }

                }
            }

            // Combine textures A and B onto texture C
            bindFramebuffer(FRAMEBUFFER, overlapCFrameBufferId)
            clearColor(0f, 0f, 0f, 0f)
            clear(COLOR_BUFFER_BIT)
            // Draw actorA
            viewport(0, 0, MAX_WIDTH, MAX_HEIGHT)
            PlainTextureProgram.blitAll(overlapATextureID)
            // Now draw actorB, but use the MINIMUM of the existing value and the to-be-drawn value.
            // The next result, only in places were the actors overlap will have a non-zero alpha value.
            blendEquation(FUNC_MIN)
            PlainTextureProgram.blitAll(overlapBTextureID)
            blendEquation(FUNC_ADD)


            val result = scanForOverlappingPixels(
                min(MAX_WIDTH, (rightA - leftA).toInt()),
                min(MAX_HEIGHT, (topA - bottomA).toInt()),
                threshold
            )

            // Stop using the frame buffer, and return to rendering to the window.
            bindFramebuffer(FRAMEBUFFER, null)

            // TODO Optimisation :
            // Copy the right side to the left side (using MAX func)
            // Copy the bottom left to the top left
            // Repeat, halving the rectangle size by 2 each time.
            // Then copy the result to a 1x1 texture, and read the result.
            // In this way, we don't need to transfer the entire texture from GPU to CPU.

            return result
        }
    }

    private fun scanForOverlappingPixels(width: Int, height: Int, threshold: Int): Boolean {

        // Read the pixel data from the destination texture
        WebGLBackend.gl.readPixels(0, 0, width, height, RGBA, UNSIGNED_BYTE, pixelData)
        val nextLineOffset = (MAX_WIDTH - width) * 4
        var index = 3 // Start at the first pixel's alpha channel
        for (y in 0 until height) {
            for (x in 0 until width) {
                val alpha = pixelData[index].toInt() and 0xff
                if (alpha > threshold) return true
                index += 4
            }
            index += nextLineOffset
        }

        return false
    }

}
